import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Package, Clock, MapPin, User, MessageSquare, AlertCircle, Star, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useMessageEncryption } from "@/hooks/useMessageEncryption";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/hooks/useLanguage";
import DOMPurify from 'dompurify';
import { formatPriceWithCurrency } from "@/utils/currency";
import { useCurrency } from "@/hooks/useCurrency";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ContactRelayButton } from "@/components/buyer/ContactRelayButton";
import CreateDisputeDialog from "@/components/buyer/CreateDisputeDialog";
import AddReviewForm from "@/components/AddReviewForm";
import { ConversationView } from "@/components/ConversationView";
import { OrderTracker } from "@/components/OrderTracker";
import PickupTokenDisplay from "@/components/PickupTokenDisplay";

// Helper function to sanitize HTML content
const sanitizeHTML = (html: string | null | undefined): string => {
  if (!html) return '';
  return DOMPurify.sanitize(html);
};
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface OrderDetails {
  id: string;
  order_number: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
  paid_at: string | null;
  available_at: string | null;
  picked_up_at: string | null;
  expires_at: string | null;
  vendor_id: string;
  relay_id: string;
  product_id: string;
  products?: {
    title: string;
    description: string;
    images: string[] | null;
  };
  relays?: {
    name: string;
    address: string;
    contact_phone: string;
  };
  vendor_profile?: {
    business_name: string | null;
    full_name: string | null;
  };
}

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslations();
  const { language } = useLanguage();
  const { displayCurrency, convertPrice } = useCurrency();
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [canCancel, setCanCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showReviewProduct, setShowReviewProduct] = useState(false);
  const [showReviewVendor, setShowReviewVendor] = useState(false);

  const fetchOrderDetails = useCallback(async () => {
    if (!orderId || !user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          products (
            title,
            description,
            images
          ),
          relays (
            name,
            address,
            contact_phone
          ),
          vendor_profile:profiles!orders_vendor_id_fkey (
            business_name,
            full_name
          )
        `)
        .eq('id', orderId)
        .eq('client_id', user.id)
        .single();

      if (error) throw error;
      setOrder(data);

      // Check if order can be cancelled
      if (data.status === 'PAID') {
        const { data: canCancelData, error: canCancelError } = await supabase
          .rpc('can_cancel_order', { order_uuid: orderId });
        
        if (!canCancelError) {
          setCanCancel(canCancelData || false);
        }
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Erreur lors du chargement de la commande');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  }, [orderId, user, navigate]);

  useEffect(() => {
    if (orderId && user) {
      fetchOrderDetails();
    }

    // Subscribe to real-time order updates
    const channel = supabase
      .channel('order-detail-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `id=eq.${orderId}`
        },
        () => {
          if (orderId && user) {
            fetchOrderDetails();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orderId, user, fetchOrderDetails]);

  const handleCancelOrder = async () => {
    if (!orderId || !order || cancelling) return;

    const reason = prompt('Veuillez indiquer la raison de l\'annulation:');
    if (!reason) return;

    try {
      setCancelling(true);
      const { data, error } = await supabase
        .rpc('cancel_order', {
          order_uuid: orderId,
          cancellation_reason: reason
        });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; message?: string };
      
      if (result.success) {
        toast.success('Commande annulée avec succès');
        fetchOrderDetails();
      } else {
        toast.error(result.error || 'Impossible d\'annuler la commande');
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error('Erreur lors de l\'annulation de la commande');
    } finally {
      setCancelling(false);
    }
  };

  const statusFallbacks: Record<string, Record<string, string>> = {
    fr: {
      PENDING_PAYMENT: 'En attente de paiement',
      PAID: 'Payé',
      PREPARING: 'En préparation',
      READY_FOR_PICKUP: 'Prêt pour retrait',
      AVAILABLE_FOR_PICKUP: 'Disponible pour retrait',
      PICKED_UP: 'Récupéré',
      CANCELLED: 'Annulé',
      EXPIRED: 'Expiré',
    },
    en: {
      PENDING_PAYMENT: 'Pending payment',
      PAID: 'Paid',
      PREPARING: 'Preparing',
      READY_FOR_PICKUP: 'Ready for pickup',
      AVAILABLE_FOR_PICKUP: 'Available for pickup',
      PICKED_UP: 'Picked up',
      CANCELLED: 'Cancelled',
      EXPIRED: 'Expired',
    },
    'lg-fr': {
      PENDING_PAYMENT: 'Ezali kozela kofuta',
      PAID: 'Efutami',
      PREPARING: 'Ezali kobongisama',
      READY_FOR_PICKUP: 'Ezali pene ya kozwa',
      AVAILABLE_FOR_PICKUP: 'Ezali pene ya kozwa',
      PICKED_UP: 'Ezuami',
      CANCELLED: 'Ebatalaki',
      EXPIRED: 'Etikali',
    },
    sw: {
      PENDING_PAYMENT: 'Inasubiri malipo',
      PAID: 'Imelipwa',
      PREPARING: 'Inaandaliwa',
      READY_FOR_PICKUP: 'Tayari kuchukua',
      AVAILABLE_FOR_PICKUP: 'Iko tayari kuchukua',
      PICKED_UP: 'Imechukuliwa',
      CANCELLED: 'Imeghairiwa',
      EXPIRED: 'Imeisha muda',
    },
  };

  const getStatusLabel = (status: string) => {
    const key = `orders.status.${status}`;
    const fallback = statusFallbacks[language]?.[status] ?? statusFallbacks['fr'][status] ?? status;
    return t(key, fallback);
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'PENDING_PAYMENT':
        return 'secondary';
      case 'PAID':
      case 'PREPARING':
      case 'READY_FOR_PICKUP':
        return 'default';
      case 'PICKED_UP':
        return 'default';
      case 'CANCELLED':
      case 'EXPIRED':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="space-y-4">
              <Card>
                <CardContent className="p-6 space-y-4">
                  <div className="h-6 bg-muted rounded w-1/3"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                  <div className="h-32 bg-muted rounded"></div>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-8">
          <div className="text-center">
            <p>Commande introuvable</p>
            <Button onClick={() => navigate('/orders')} className="mt-4">
              Retour aux commandes
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/orders')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux commandes
          </Button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">
                Commande {order.order_number}
              </h1>
              <p className="text-muted-foreground">
                Passée le {new Date(order.created_at).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <Badge variant={getStatusVariant(order.status) as any} className="text-lg px-4 py-2">
              {getStatusLabel(order.status)}
            </Badge>
          </div>

          {/* Cancel warning */}
          {canCancel && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4 flex items-start gap-3">
                <Clock className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-orange-900">
                    Vous pouvez encore annuler cette commande dans les 30 minutes suivant le paiement.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Produit commandé
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  {order.products?.images?.[0] && (
                    <div className="w-24 h-24 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={order.products.images[0]}
                        alt={order.products.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-2">
                      {order.products?.title || 'Produit'}
                    </h3>
                    <div 
                      className="text-muted-foreground text-sm mb-3 line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: sanitizeHTML(order.products?.description || '') }}
                    />
                    <p className="text-2xl font-bold text-primary">
                      {formatPriceWithCurrency(order.amount_cents, order.currency, displayCurrency, convertPrice)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vendor Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Vendeur
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">
                      {order.vendor_profile?.business_name || order.vendor_profile?.full_name || 'Vendeur'}
                    </p>
                  </div>
                  <ContactVendorDialog 
                    vendorId={order.vendor_id}
                    vendorName={order.vendor_profile?.business_name || order.vendor_profile?.full_name || 'Vendeur'}
                    orderId={order.id}
                    orderNumber={order.order_number}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Relay Point Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Point de retrait
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <p className="font-semibold break-words">{order.relays?.name}</p>
                    <p className="text-sm text-muted-foreground break-words">
                      {order.relays?.address}
                    </p>
                    <p className="text-sm text-muted-foreground break-words">
                      Tél: {order.relays?.contact_phone}
                    </p>
                  </div>
                  <ContactRelayButton 
                    relayId={order.relay_id}
                    relayName={order.relays?.name || 'Point relais'}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Order Tracker */}
            <Card>
              <CardHeader>
                <CardTitle>Suivi de commande</CardTitle>
              </CardHeader>
              <CardContent>
                <OrderTracker
                  createdAt={order.created_at}
                  paidAt={order.paid_at}
                  availableAt={order.available_at}
                  pickedUpAt={order.picked_up_at}
                  status={order.status}
                />
              </CardContent>
            </Card>

            {/* QR Code for Pickup */}
            {(order.status === 'PAID' || order.status === 'AVAILABLE_FOR_PICKUP' || order.status === 'PICKED_UP') && (
              <Card>
                <CardHeader>
                  <CardTitle>Code de retrait</CardTitle>
                </CardHeader>
                <CardContent>
                  <PickupTokenDisplay orderId={order.id} />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Cancel Order */}
                {canCancel && (
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={handleCancelOrder}
                    disabled={cancelling}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    {cancelling ? 'Annulation...' : 'Annuler la commande'}
                  </Button>
                )}

                <Separator />

                {/* Review Product */}
                {order.status === 'PICKED_UP' && (
                  <>
                    <Dialog open={showReviewProduct} onOpenChange={setShowReviewProduct}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full">
                          <Star className="h-4 w-4 mr-2" />
                          Noter le produit
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Noter le produit</DialogTitle>
                          <DialogDescription>
                            {order.products?.title}
                          </DialogDescription>
                        </DialogHeader>
                        <AddReviewForm
                          productId={order.product_id}
                          onReviewAdded={() => {
                            setShowReviewProduct(false);
                            toast.success('Merci pour votre avis !');
                          }}
                          onCancel={() => setShowReviewProduct(false)}
                        />
                      </DialogContent>
                    </Dialog>

                    {/* Review Vendor */}
                    <VendorReviewDialog
                      vendorId={order.vendor_id}
                      vendorName={order.vendor_profile?.business_name || order.vendor_profile?.full_name || 'Vendeur'}
                    />
                  </>
                )}

                <Separator />

                {/* Create Dispute */}
                <CreateDisputeDialog
                  orderId={order.id}
                  orderNumber={order.order_number}
                  productTitle={order.products?.title || 'Produit'}
                  onDisputeCreated={() => {
                    toast.success('Réclamation créée');
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

// Timeline Item Component
const TimelineItem = ({ 
  label, 
  date, 
  completed 
}: { 
  label: string; 
  date: string | null; 
  completed: boolean;
}) => {
  return (
    <div className="flex items-start gap-3">
      <div className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ${
        completed ? 'bg-green-500' : 'bg-muted'
      }`} />
      <div className="flex-1">
        <p className={`font-medium ${completed ? 'text-foreground' : 'text-muted-foreground'}`}>
          {label}
        </p>
        {date && (
          <p className="text-xs text-muted-foreground">
            {new Date(date).toLocaleDateString('fr-FR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        )}
      </div>
    </div>
  );
};

// Contact Vendor Dialog - with Conversation View
const ContactVendorDialog = ({ vendorId, vendorName, orderId, orderNumber }: { vendorId: string; vendorName: string; orderId: string; orderNumber: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <MessageSquare className="h-4 w-4 mr-2" />
          Contacter
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl h-[80vh] p-0">
        <div className="h-full flex flex-col">
          <ConversationView
            otherUserId={vendorId}
            otherUserName={vendorName}
            initialSubject={`Commande ${orderNumber}`}
            orderId={orderId}
            onBack={() => setOpen(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Vendor Review Dialog
const VendorReviewDialog = ({ vendorId, vendorName }: { vendorId: string; vendorName: string }) => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const { sendEncryptedMessage } = useMessageEncryption();

  const handleSubmit = async () => {
    if (!user || rating === 0 || !comment.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      setSubmitting(true);
      
      // Send encrypted vendor review message
      const { error } = await sendEncryptedMessage({
        recipientId: vendorId,
        subject: `Avis vendeur - ${rating} étoiles`,
        message: comment.trim()
      });

      if (error) throw error;

      toast.success('Merci pour votre avis sur le vendeur !');
      setOpen(false);
      setRating(0);
      setComment('');
    } catch (error) {
      console.error('Error submitting vendor review:', error);
      toast.error('Erreur lors de la publication de l\'avis');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          <Star className="h-4 w-4 mr-2" />
          Noter le vendeur
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Noter {vendorName}</DialogTitle>
          <DialogDescription>
            Partagez votre expérience avec ce vendeur
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Note</Label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`h-8 w-8 ${
                      star <= (hoveredRating || rating)
                        ? "fill-primary text-primary"
                        : "text-muted-foreground"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Commentaire</Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Décrivez votre expérience avec ce vendeur..."
              rows={5}
              maxLength={1000}
            />
          </div>
          <Button
            onClick={handleSubmit}
            disabled={submitting || rating === 0 || !comment.trim()}
            className="w-full"
          >
            {submitting ? 'Publication...' : 'Publier l\'avis'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetail;
